import { estCleQcmUnique } from './modePassation';

/**
 * Composition AUTOMATIQUE d'un attendu (chantier composition, étape 5,
 * 2026-09-02) : « Composer N questions sur cet attendu ». Module pur, le
 * générateur est injecté (testable avec un registre factice).
 *
 * Règles :
 *   - une composition REMPLACE la précédente : les exercices ajoutés
 *     automatiquement pour cet attendu (`_meta.compoAuto`) sont tous retirés,
 *     puis N sont composés avec la cible demandée — « 5 variés » puis « 5 QCM
 *     pour les cartes » donne 5 QCM, pas 10 exercices (retour prof
 *     2026-09-02). Les exercices ajoutés À LA MAIN restent et comptent dans N ;
 *     les autres attendus du panier ne comptent pas ;
 *   - tourne sur les cartes de l'attendu : une instance par carte, puis un
 *     second tour, etc. — cible « cartes » : seulement le format QCM à
 *     réponse unique de chaque carte (carte sans QCM sautée) ; cible « varié » :
 *     un format différent à chaque tour (saisie, QCM, trous…) ;
 *   - instances DISTINCTES (empreinte texte + contenu), 5 essais par tirage ;
 *     une carte qui ne sait plus rien donner de neuf est mise de côté ;
 *   - s'arrête quand N est atteint ou quand plus aucune carte ne peut donner :
 *     `manque` dit ce qui n'a pas pu être ajouté (le bouton le montre).
 * `exclus` : instances déjà données ailleurs (le noyau des groupes), jamais
 * reprises.
 * Chaque ajout porte la clé de panier et le contexte pédagogique de « + Ajouter »
 * (descripteur, intitulé d'attendu) : même rangement, même impression.
 *
 * @returns {{ ajouts: Array<{ cle: string, payload: object }>, retraits: Array<{ cle: string, payloads: Array }>, nbRetires: number, manque: number, nbManuels: number }}
 */
export const TAILLES = [5, 8, 10];
export const TAILLE_DEFAUT = 8;
export const CIBLE_CARTES = 'cartes';
export const CIBLE_VARIE = 'varie';

const ESSAIS_DOUBLON = 5;
const TOURS_MAX = 50;

const empreinte = (p) => JSON.stringify([p?.texte ?? '', p?.contenu ?? null]);

/** Formats candidats d'une carte selon la cible. */
const formatsPourCible = (cat, cible) =>
    (cat?.formatsList || []).filter((f) => (cible === CIBLE_CARTES ? estCleQcmUnique(f) : true));

/** Vrai si au moins une carte de l'attendu peut servir cette cible. */
export const ciblePossible = (cartes, cible) => (cartes || []).some((cat) => formatsPourCible(cat, cible).length > 0);

/**
 * Une composition REMPLACE la précédente : toutes les instances automatiques
 * de l'attendu (marquées `_meta.compoAuto`) sont retirées avant de composer ;
 * jamais un exercice ajouté à la main. Retourne, par clé touchée, la liste des
 * instances à GARDER (les manuelles) et le nombre retiré.
 */
const retirerAutos = (dejaPris, prefixe) => {
    const retraits = [];
    let nbRetires = 0;
    let nbManuels = 0;
    const empreintes = new Set();
    if (dejaPris && typeof dejaPris.forEach === 'function') {
        dejaPris.forEach((entree, cle) => {
            if (!String(cle).startsWith(prefixe)) return;
            const payloads = entree?.payloads || [];
            const manuels = payloads.filter((p) => !p?._meta?.compoAuto);
            nbManuels += manuels.length;
            manuels.forEach((p) => empreintes.add(empreinte(p)));
            if (manuels.length < payloads.length) {
                nbRetires += payloads.length - manuels.length;
                retraits.push({ cle, payloads: manuels });
            }
        });
    }
    return { retraits, nbRetires, nbManuels, empreintes };
};

export const composerAuto = ({ cartes, attenduId, cible, n, dejaPris, generateExercice, contexte = {}, exclus = [] }) => {
    const prefixe = `${attenduId}_`;
    // 1. La composition précédente s'efface (les choix manuels restent et comptent).
    const { retraits, nbRetires, nbManuels, empreintes } = retirerAutos(dejaPris, prefixe);
    exclus.forEach((p) => empreintes.add(empreinte(p)));
    const ajouts = [];
    const aAjouter = Math.max(0, (n || 0) - nbManuels);
    if (aAjouter === 0) return { ajouts, retraits, nbRetires, manque: 0, nbManuels };

    const plans = (cartes || [])
        .map((cat) => ({ cat, formats: formatsPourCible(cat, cible) }))
        .filter((p) => p.formats.length > 0);
    if (plans.length === 0) return { ajouts, retraits, nbRetires, manque: aAjouter, nbManuels };

    const epuisees = new Set();
    for (let tour = 0; tour < TOURS_MAX && ajouts.length < aAjouter && epuisees.size < plans.length; tour++) {
        for (const plan of plans) {
            if (ajouts.length >= aAjouter) break;
            if (epuisees.has(plan.cat.id)) continue;
            const fmt = plan.formats[tour % plan.formats.length];
            let payload = null;
            for (let essai = 0; essai < ESSAIS_DOUBLON && !payload; essai++) {
                let gen = null;
                try { gen = generateExercice(attenduId, fmt.key); } catch { gen = null; }
                if (!gen) break;
                if (!empreintes.has(empreinte(gen))) payload = gen;
            }
            if (!payload) { epuisees.add(plan.cat.id); continue; }
            empreintes.add(empreinte(payload));
            ajouts.push({
                cle: `${attenduId}_${plan.cat.id}_${fmt.key}`,
                payload: {
                    ...payload,
                    attendu: payload.attendu ?? attenduId,
                    _meta: {
                        ...(payload._meta || {}),
                        ...(contexte.descName ? { descripteur: contexte.descName } : {}),
                        ...(contexte.attenduDescription ? { attenduTitre: contexte.attenduDescription } : {}),
                        compoAuto: true, // retirable par un « Composer N » plus petit
                    },
                },
            });
        }
    }
    return { ajouts, retraits, nbRetires, manque: aAjouter - ajouts.length, nbManuels };
};
